import React, { useContext, useEffect, useRef, useState } from 'react'
import range from 'lodash/range'
import { FormattedMessage } from 'react-intl'
import Table from 'components/Table'
import ItemsPerPageDropdown from 'components/ItemsPerPageDropdown'
import Pagination from 'features/Pagination'
import sortDirection from 'constants/sortDirection'
import Select from 'components/Select'
import siteStatuses, {
  SITE_STATUS_CONFIRMED_ID,
  SITE_STATUS_SUPERSEDED_ID,
  SITE_STATUS_DELISTED_ID,
  SITE_STATUS_EDIT_ONLY_ID,
  SITE_STATUS_SUBMITTED_EDIT_ONLY_ID
} from 'constants/siteStatuses'
import {
  requestConfirmState,
  requestGetSites,
  requestNominateSite,
  requestReviewByRfp,
  requestAllSitesReassessments,
  deleteSiteRequest,
  requestDoesNotQualify,
  requestGetSitesSearch,
  requestReassesSite,
  requestDelistState,
  requestEditSite,
  requestApproveEdits,
  deleteEditSiteOnlyRequest
} from 'api/site'
import { usePersistPagination } from 'hooks/usePersistPagination'
import Modal from 'components/Modal'
import SiteSupersedeModal from './SiteModals/SiteSupersedeModal'
import AssessmentModal from './SiteModals/AssessmentModal'
import { isKbaStatusEligibleForSupersedeModal } from 'utils/supersedeMenuHelpers'
import { INDIVIDUAL_ROLE_ID, NCG_ROLE_ID, RFP_ROLE_ID, SECRETARIAT_ROLE_ID, BRC_ROLE_ID } from 'constants/roles'
import { canReviewSites, canConfirmSites, getProfileRoleId } from 'utils/rolePermissions'
import { resolveEditOnlyRouteIds } from 'utils/editOnlyNavigation'
import {
  adjustPaginationForFilteredSites,
  filterSitesForIndividualDisplay
} from 'utils/sitesListVisibility'
import SearchInput from 'components/SearchInput'
import GlobeIcon from 'assets/icons/sidebar-sites'
import { requestAllEvaluationsForSite, requestDeleteMonitoringDraft, requestRevertMonitoringDraft } from 'api/monitoring'

import {
  SiteFilterValueWrapper,
  SitesFilterItemWrapper,
  SitesFilterSearchWrapper,
  SitesFilterWrapper,
  SitesFooterWrapper,
  SitesTableIconConfirmed,
  SitesTableIconDelistedWrapper,
  SitesTableIconDraft,
  SitesTableIconEditOnly,
  SitesTableIconNominated,
  SitesTableIconNotAssessed,
  SitesTableIconNotQualifyWrapper,
  SitesTableIconProposed,
  SitesTableIconReviewedRFP,
  SitesTableIconSubmittedEdits,
  SitesTableIconSuperseded,
  SitesTableWrapper,
  SiteTableIconDelisted
} from './style'
import { makeSitesColumns, makeSitesRows } from './sitesTable'
import SiteRevertStatusModal from './SiteModals/SiteRevertStatusModal'
import FlowRequestModal from 'components/FlowRequestModal'
import { ProfileContext } from 'providers/profile'
import { MySiteContext } from 'providers/mySiteProvider'
import { showErrorToast } from 'utils/toast'
import { CountriesContext } from 'providers/countries'
import { SitesSearchInputWrapper, SitesSearchWrapper, ResponsiveComponent } from 'pages/SitesManagement/style'
import { SitesModalContext } from 'providers/sitesProviders'
import { useIBAAccess } from 'hooks/useIBAAccess'
import { useNavigate } from 'react-router-dom'
import { buildAboutUrl, buildDetailsUrl, editOnly, SITES_MAP_PATH } from 'routing/constants'
import { resolveSiteHistoryId } from 'utils/navigationHandlers'
import Button from 'components/Button'
import LoadingWrapper from 'components/LoadingWrapper'
import {
  isSitesListSearchQueryActive,
  normalizeSitesFilterMulti,
  serializeOptionValuesParam
} from './siteListQueryParams'

const statusMapping = {
  Draft: (
    <SiteFilterValueWrapper>
      <SitesTableIconDraft />
      Draft
    </SiteFilterValueWrapper>
  ),
  Proposed: (
    <SiteFilterValueWrapper>
      <SitesTableIconProposed />
      Proposed
    </SiteFilterValueWrapper>
  ),
  Nominated: (
    <SiteFilterValueWrapper>
      <SitesTableIconNominated />
      Nominated
    </SiteFilterValueWrapper>
  ),
  'Reviewed by RFP': (
    <SiteFilterValueWrapper>
      <SitesTableIconReviewedRFP />
      Reviewed by RFP
    </SiteFilterValueWrapper>
  ),
  Confirmed: (
    <SiteFilterValueWrapper>
      <SitesTableIconConfirmed />
      Confirmed
    </SiteFilterValueWrapper>
  ),
  Superseded: (
    <SiteFilterValueWrapper>
      <SitesTableIconSuperseded />
      Superseded
    </SiteFilterValueWrapper>
  ),
  Delisted: (
    <SiteFilterValueWrapper>
      <SitesTableIconDelistedWrapper>
        <SiteTableIconDelisted />
      </SitesTableIconDelistedWrapper>
      Delisted
    </SiteFilterValueWrapper>
  ),
  'Does not Qualify': (
    <SiteFilterValueWrapper>
      <SitesTableIconNotQualifyWrapper />
      Does not Qualify
    </SiteFilterValueWrapper>
  ),
  'Edit Only': (
    <SiteFilterValueWrapper>
      <SitesTableIconEditOnly />
      Edit only
    </SiteFilterValueWrapper>
  ),
  'Submitted Edits': (
    <SiteFilterValueWrapper>
      <SitesTableIconSubmittedEdits />
      Edits submitted
    </SiteFilterValueWrapper>
  ),
  'Not assessed': (
    <SiteFilterValueWrapper>
      <SitesTableIconNotAssessed />
      Not assessed
    </SiteFilterValueWrapper>
  )
}

const formattedStatus = siteStatuses
  .slice(4)
  .filter((status) => status.id !== SITE_STATUS_EDIT_ONLY_ID && status.id !== SITE_STATUS_SUBMITTED_EDIT_ONLY_ID)
  .map((status) => {
    return {
      label: statusMapping[status.name],
      value: status.id
    }
  })

const currentYear = new Date().getFullYear()

const formattedYears = range(currentYear, 1949, -1).map((year) => {
  return {
    label: year,
    value: year
  }
})

let sitesFetchAbortController = null

const buildSiteListParams = ({
  searchSitesQuery,
  pagination,
  sorting,
  isMySite,
  statusesSelection,
  yearsSelection,
  countriesSelection,
  signal
}) => {
  const normalizedSearchQuery = searchSitesQuery.toLowerCase().trim()
  const isSearchMode = isSitesListSearchQueryActive(searchSitesQuery)
  const baseParams = {
    resultsPerPage: pagination.resultsPerPage,
    currentPage: pagination.currentPage,
    sortBy: sorting.sortBy,
    sortDirection: sorting.sortDirection,
    toggle: isMySite,
    signal
  }
  const countryParam = serializeOptionValuesParam(countriesSelection)
  const yearParam = serializeOptionValuesParam(yearsSelection)
  const statusParam = serializeOptionValuesParam(statusesSelection)

  if (isSearchMode) {
    return {
      request: requestGetSitesSearch,
      params: {
        ...baseParams,
        query: normalizedSearchQuery,
        country: countryParam,
        year: yearParam,
        siteStatus: statusParam
      }
    }
  }

  return {
    request: requestGetSites,
    params: {
      ...baseParams,
      siteStatus: statusParam,
      year: yearParam,
      country: countryParam
    }
  }
}

const SitesManagement = () => {
  const { profile } = useContext(ProfileContext)
  const { isModalOpen, toggleModal, siteData, setSite } = useContext(SitesModalContext)
  const {
    isMySite,
    getSitesCount,
    refreshSites,
    updateSitesFilters,
    siteFilters,
    sites: filteredSites,
    updateFilteredSites,
    pagination,
    isLoading: tableLoadingStatus,
    sitesListRefreshNonce,
    setIsLoading: setMySiteContextLoading
  } = useContext(MySiteContext)
  const { countriesOptions } = useContext(CountriesContext)
  const { setPagination, selectPage, nextPage, prevPage, nextSection, prevSection, changeResultsPerPage } =
    usePersistPagination()
  const [countriesSelection, setCountriesSelection] = useState(() => normalizeSitesFilterMulti(siteFilters.country))
  const [statusesSelection, setStatusesSelection] = useState(() => normalizeSitesFilterMulti(siteFilters.status))
  const [yearsSelection, setYearsSelection] = useState(() => normalizeSitesFilterMulti(siteFilters.year))
  const isFirstMySiteEffectRef = useRef(true)
  const [sites, setSites] = useState(filteredSites)
  const [reassessmentSites, setReassessmentSites] = useState([])
  const [siteToWorkWith, setSiteToWorkWith] = useState({})
  const [siteToReject, setSiteToReject] = useState({})
  const [editOnlySiteToReject, setEditOnlySiteToReject] = useState(false)
  const [siteToPublish, setSiteToPublish] = useState(false)
  const [approveEditOnlyCallBack, setApproveEditOnlyCallBack] = useState(false)
  const [siteToDelist, setSiteToDelist] = useState(false)
  const [isSiteDoesNotQualified, setIsSiteDoesNotQualified] = useState(false)
  const [siteIsModalEditOpen, setSiteIsModalEditOpen] = useState({})
  const [biodiversitySites, setBiodiversitySites] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('assessments')

  const isBiodiversity = activeTab === 'biodiversity'
  const modalTitleId =
    isBiodiversity && biodiversitySites.length
      ? 'allBiodiversityEvaluations'
      : isBiodiversity
        ? 'noBiodiversityEvaluations'
        : 'allAssessments'

  const assessmentValue = isBiodiversity ? biodiversitySites.length : reassessmentSites.length

  const [siteForDeleting, setSiteForDeleting] = useState({})
  const [searchSitesQuery, setSearchSitesQuery] = useState('')
  const [monitoringForDeleting, setMonitoringForDeleting] = useState({})
  const [monitoringForReverting, setMonitoringForReverting] = useState({})
  const history = useNavigate()
  const { shouldShowSiteIBAData, canSeeIBASitesManagement } = useIBAAccess()

  const assessSite = () => {
    requestReassesSite(siteData.id)
      .then((data) => {
        if (data.error) {
          return showErrorToast(data.error, 'showBorder')
        }

        toggleModal()
        setSite({
          id: siteData.id
        })
        history(buildDetailsUrl(siteData.id, data.historyId))
      })
      .catch((error) => {
        showErrorToast(error?.message || 'An unexpected error occurred.', 'showBorder')
      })
  }

  async function editNowSite() {
    try {
      const data = await requestEditSite(siteIsModalEditOpen.id)

      if (data.error) {
        return showErrorToast(data.error, 'showBorder')
      }

      // Must use SitesEditOnly PK — never sitehistoryId / historyId.
      const editOnlySiteId = data.id
      const parentSiteId = data.siteId || siteIsModalEditOpen.id

      setSite({
        id: siteIsModalEditOpen.id
      })
      history(buildAboutUrl(parentSiteId, editOnlySiteId, editOnly))
    } catch (error) {
      showErrorToast(error?.message || 'An unexpected error occurred.', 'showBorder')
    }
  }
  const [sorting, setSorting] = useState({
    sortBy: 'name',
    sortDirection: sortDirection.ASC
  })

  function closeDelistModal() {
    setSiteToDelist(false)
  }
  function closeDoesNotQualifyModal() {
    setIsSiteDoesNotQualified(false)
  }

  function getSites() {
    // Abort previous fetch request, if exists
    if (sitesFetchAbortController) {
      setIsLoading(false)
      sitesFetchAbortController.abort()
    }
    sitesFetchAbortController = new AbortController()
    const { request, params } = buildSiteListParams({
      searchSitesQuery,
      pagination,
      sorting,
      isMySite,
      statusesSelection,
      yearsSelection,
      countriesSelection,
      signal: sitesFetchAbortController.signal
    })

    setIsLoading(true)

    request(params)
      .then((data) => {
        setIsLoading(false)
        const requestedSites = data.sites || []
        const visibleSites = filterSitesForIndividualDisplay(requestedSites, profile, isMySite)
        setSites(visibleSites)
        updateFilteredSites(visibleSites)
        setPagination(adjustPaginationForFilteredSites(
          data.pagination,
          requestedSites,
          visibleSites,
          profile,
          isMySite
        ))
      })
      .catch((error) => {
        setIsLoading(false)
        if (error.name === 'AbortError') {
          return
        }
        showErrorToast(error?.message || 'An unexpected error occurred.', 'showBorder')
      })
  }

  function delistSite(site) {
    requestDelistState(site.historyId, { isApproved: site.isApproved }).then((data) => {
      setSiteToDelist(false)
      getSites()
    })
  }

  useEffect(() => {
    getSites()
  }, [
    isMySite,
    pagination?.currentPage,
    sorting.sortBy,
    sorting.sortDirection,
    pagination?.resultsPerPage,
    countriesSelection,
    statusesSelection,
    yearsSelection,
    searchSitesQuery
  ])

  useEffect(() => {
    setPagination((s) => ({ ...s, currentPage: 1 }))
    setSites([])
    updateFilteredSites([])
    // Skip initial mount so persisted Assessment filters are kept. On toggle,
    // catalog vs My Sites use different status sets — clear leftover filters
    // (e.g. Confirmed) that would hide Proposed rows.
    if (isFirstMySiteEffectRef.current) {
      isFirstMySiteEffectRef.current = false
      return
    }
    setStatusesSelection(null)
    updateSitesFilters('status', null)
  }, [isMySite])

  useEffect(() => {
    if (sitesListRefreshNonce === 0) return
    getSites()
  }, [sitesListRefreshNonce])

  function deleteSite() {
    const parentSiteId = siteForDeleting.id
    deleteSiteRequest(
      siteForDeleting.id,
      siteForDeleting.reassessmentFlag,
      siteForDeleting.historyId
    ).then((data) => {
      if (data.error) {
        return showErrorToast(data.error, 'showBorder')
      }
      setSiteForDeleting({})
      getSites()
      getSitesCount(true)
      if (reassessmentSites.length > 0 && parentSiteId) {
        getSitesReassessments(parentSiteId)
      }
    })
  }

  async function deleteMonitoring() {
    try {
      const data = await requestDeleteMonitoringDraft(monitoringForDeleting.id)
      if (data?.error) {
        return showErrorToast(data.error, 'showBorder')
      }
      setMonitoringForDeleting({})
      getSites()
      getSitesCount(true)
    } catch (err) {
      throw new Error(err)
    }
  }

  async function revertMonitoring() {
    try {
      const data = await requestRevertMonitoringDraft(monitoringForReverting.id)
      if (data?.error) {
        return showErrorToast(data.error, 'showBorder')
      }
      setMonitoringForReverting({})
      getSites()
      getSitesCount(true)
    } catch (err) {
      throw new Error(err)
    }
  }

  function handleCountriesChange(selected) {
    const next = selected?.length ? selected : null
    setCountriesSelection(next)
    updateSitesFilters('country', next)
    setPagination((s) => ({ ...s, currentPage: 1 }))
  }

  function handleStatusesChange(selected) {
    const next = selected?.length ? selected : null
    setStatusesSelection(next)
    updateSitesFilters('status', next)
    setPagination((s) => ({ ...s, currentPage: 1 }))
  }

  function handleYearsChange(selected) {
    const next = selected?.length ? selected : null
    setYearsSelection(next)
    updateSitesFilters('year', next)
    setPagination((s) => ({ ...s, currentPage: 1 }))
  }

  const clearFilters = () => {
    setCountriesSelection(null)
    setYearsSelection(null)
    setStatusesSelection(null)
    updateSitesFilters('year', null)
    updateSitesFilters('country', null)
    updateSitesFilters('status', null)
  }

  const rfpAction = async (site, isApproved) => {
    try {
      await requestReviewByRfp(site.id, site.historyId, isApproved);
      setSiteToWorkWith(false);
      setSiteToReject(false);
      getSites();
    } catch (error) {
      showErrorToast(error?.message || 'An unexpected error occurred.', 'showBorder')
    }
  };

  const nominateSite = async (site) => {
    try {
      await requestNominateSite(site.id, site.historyId);
      setSiteToWorkWith(false);
      getSites();
    } catch (error) {
      showErrorToast(error?.message || 'An unexpected error occurred.', 'showBorder')
    }
  };

  const publishAction = async (site) => {
    try {
      const historyId = resolveSiteHistoryId(site)
      if (site.reassessmentFlag && !historyId) {
        showErrorToast('Missing reassessment history identifier', 'showBorder')
        return
      }
      const response = await requestConfirmState(site.id, historyId, { isApproved: site.isApproved })
      if (response?.error) {
        return showErrorToast(
          typeof response.error === 'string' ? response.error : 'Failed to confirm assessment',
          'showBorder'
        )
      }
      setSiteToPublish(false)
      refreshSites()
      getSites()
    } catch (error) {
      showErrorToast(error?.message || 'An unexpected error occurred.', 'showBorder')
    }
  }

  async function deleteEditOnlySite() {
    try {
      const parentSiteId = editOnlySiteToReject.siteId || editOnlySiteToReject.id
      const data = await deleteEditSiteOnlyRequest(editOnlySiteToReject.id, editOnlySiteToReject.siteStatusId)
      if (data?.error) {
        return showErrorToast(data.error, 'showBorder')
      }
      setEditOnlySiteToReject(false);
      getSites();
      if (reassessmentSites.length > 0 && parentSiteId) {
        getSitesReassessments(parentSiteId)
      }
    } catch (error) {
      showErrorToast(error?.message || 'An unexpected error occurred.', 'showBorder')
    }
  }

  const approveEditsAction = async (site) => {
    try {
      const { siteId, editOnlySiteId } = resolveEditOnlyRouteIds(site)
      await requestApproveEdits(editOnlySiteId, siteId, { isApproved: site.isApproved });
      setApproveEditOnlyCallBack(false);
      getSites();
    } catch (error) {
      showErrorToast(error?.message || 'An unexpected error occurred.', 'showBorder')
    }
  }

  const doesNotQualify = async (site) => {
    try {
      await requestDoesNotQualify(site.id);
      setIsSiteDoesNotQualified(false);
      getSites();
    } catch (error) {
      showErrorToast(error?.message || 'An unexpected error occurred.', 'showBorder')
    }
  };

  const getSitesReassessments = async (siteId) => {
    try {
      const data = await requestAllSitesReassessments(siteId)
      // Backend controls reassessment visibility, so keep the payload as-is.
      setReassessmentSites(Array.isArray(data) ? data : [])
    } catch (error) {
      showErrorToast(error?.message || 'An unexpected error occurred.', 'showBorder')
    }
  };

  const getBiodiversityEvaluations = async (siteId) => {
    try {
      const data = await requestAllEvaluationsForSite(siteId)
      setBiodiversitySites(data)
    } catch (error) {
      throw new Error(error)
    }
  }

  function handleSearch(e) {
    const nextQuery = e.target.value
    const wasSearchMode = isSitesListSearchQueryActive(searchSitesQuery)
    const isSearchMode = isSitesListSearchQueryActive(nextQuery)
    setSearchSitesQuery(nextQuery)
    if (wasSearchMode || isSearchMode) {
      setPagination((s) => {
        return { ...s, currentPage: 1 }
      })
    }
  }

  const showReassessmentListModal = (item) => {
    setActiveTab('assessments')
    getSitesReassessments(item.id)
    getBiodiversityEvaluations(item.id)
  }

  const columns = makeSitesColumns(sorting, setSorting, isMySite, profile, canSeeIBASitesManagement)
  const rows = makeSitesRows(
    {
      setSiteToWorkWith,
      setSiteToReject,
      setSiteToPublish,
      setSiteToDelist,
      showReassessmentListModal,
      isMySite,
      setSiteForDeleting,
      setIsSiteDoesNotQualified,
      setSiteIsModalEditOpen,
      approveEditOnlyCallBack: setApproveEditOnlyCallBack,
      setEditOnlySiteToReject,
      setMonitoringForDeleting,
      setMonitoringForReverting,
    },
    {
      profile,
      navigate: history,
      toggleModal,
      setSite,
      setIsLoading: setMySiteContextLoading,
      countriesOptions,
      shouldShowSiteIBAData,
    },
    sites
  )

  const setEmptyArray = () => {
    setBiodiversitySites([])
    setActiveTab('assessments')
    return setReassessmentSites([])
  }

  const profileRoleId = getProfileRoleId(profile)
  const usePreConfirmedAssessmentStatuses =
    profileRoleId === RFP_ROLE_ID ||
    profileRoleId === BRC_ROLE_ID ||
    profileRoleId === SECRETARIAT_ROLE_ID
  const formattedStatusAssessment = siteStatuses
    .filter((item) => {
      return usePreConfirmedAssessmentStatuses
        ? item.id < SITE_STATUS_CONFIRMED_ID ||
        item.id === SITE_STATUS_EDIT_ONLY_ID ||
        item.id === SITE_STATUS_SUBMITTED_EDIT_ONLY_ID
        : item.id < SITE_STATUS_SUPERSEDED_ID ||
        item.id === SITE_STATUS_EDIT_ONLY_ID ||
        item.id === SITE_STATUS_SUBMITTED_EDIT_ONLY_ID
    })
    .map((status) => {
      return {
        label: statusMapping[status.name],
        value: status.id
      }
    })

  // consider making the view on map button a link that is only styled like a button...
  const handleViewOnMap = () => {
    history(SITES_MAP_PATH)
  }

  return (
    <>
      <Modal
        isOpen={isModalOpen}
        toggleModal={toggleModal}
        modalTitleId="Are you sure you want to complete a reassessment for this site?"
        width="616px"
        padding="0 32px 32px 32px"
      >
        <p style={{ fontSize: '14px', lineHeight: '24px' }}>
          <FormattedMessage id="reassessmentMessage" />
        </p>

        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button height="40px" marginTop="0px" onClick={toggleModal} type="delist">
            Cancel
          </Button>
          <Button height="40px" marginTop="0px" onClick={assessSite} type="primary">
            Continue{' '}
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={siteIsModalEditOpen?.id && siteIsModalEditOpen?.SiteStatus?.id == SITE_STATUS_CONFIRMED_ID || siteIsModalEditOpen?.SiteStatus?.id == SITE_STATUS_DELISTED_ID}
        toggleModal={setSiteIsModalEditOpen}
        modalTitleId="Are you sure you want to proceed with editing this site?"
        width="616px"
        padding="0 32px 32px 32px"
      >
        <p style={{ fontSize: '14px', lineHeight: '24px' }}>
          {' '}
          <FormattedMessage id="editOnlyMessage" />
        </p>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            height="40px"
            marginTop="0px"
            onClick={() => {
              setSiteIsModalEditOpen({})
            }}
            type="delist"
          >
            Cancel
          </Button>
          <Button height="40px" marginTop="0px" onClick={editNowSite} type="primary">
            Continue{' '}
          </Button>
        </div>
      </Modal>

      <SitesFilterSearchWrapper>
        <SitesFilterWrapper data-test="quick-filter">
          <SitesFilterItemWrapper>
            <Select
              field="country"
              value={countriesSelection}
              onChange={handleCountriesChange}
              options={countriesOptions}
              placeholder="Country"
              height="40px"
              marginTop="10px"
              isMulti
              isClearable
              multiMenuRemove
              searchInMenu
            />
          </SitesFilterItemWrapper>
          <SitesFilterItemWrapper>
            <Select
              field="year"
              value={yearsSelection}
              onChange={handleYearsChange}
              options={formattedYears}
              placeholder="Year"
              height="40px"
              marginTop="0px"
              isMulti
              isClearable
              multiMenuRemove
              searchInMenu
            />
          </SitesFilterItemWrapper>
          {isMySite ? (
            <SitesFilterItemWrapper>
              <Select
                field="assessment"
                value={statusesSelection}
                onChange={handleStatusesChange}
                options={formattedStatusAssessment}
                placeholder="Assessment"
                height="40px"
                marginTop="0px"
                isMulti
                isClearable
                multiMenuRemove
                searchInMenu
              />
            </SitesFilterItemWrapper>
          ) : (
            <SitesFilterItemWrapper>
              <Select
                field="status"
                value={statusesSelection}
                onChange={handleStatusesChange}
                options={formattedStatus}
                placeholder="Status"
                height="40px"
                marginTop="0px"
                isMulti
                isClearable
                multiMenuRemove
                searchInMenu
              />
            </SitesFilterItemWrapper>
          )}
        </SitesFilterWrapper>
        <Button onClick={handleViewOnMap} marginTop="0" type="accent" withIcon>
          <GlobeIcon />
          <FormattedMessage id="mapView" />
        </Button>
        <ResponsiveComponent>
          <SitesSearchWrapper>
            <SitesSearchInputWrapper>
              <SearchInput
                value={searchSitesQuery}
                onChange={handleSearch}
                placeholder="searchSitePlaceholder"
                type="text"
                dataTest="search-input"
              />
            </SitesSearchInputWrapper>
          </SitesSearchWrapper>
        </ResponsiveComponent>
      </SitesFilterSearchWrapper>
      <SitesTableWrapper>
        <LoadingWrapper size={'24px'} isLoading={tableLoadingStatus}>
          <Table
            columns={columns}
            rows={rows}
            clearFilters={clearFilters}
            noResultMessage="noSitesMatchFilteringCriteria"
            dataTest="sites-list"
            isLoading={isLoading}
          />
        </LoadingWrapper>
        <SitesFooterWrapper data-test="pagination">
          <ItemsPerPageDropdown
            currentPage={pagination.currentPage}
            resultsPerPage={pagination.resultsPerPage}
            totalNumber={pagination.totalNumber}
            onChange={changeResultsPerPage}
            dataTest="sites-items-per-page"
            menuPlacement="top"
          />
          {pagination.totalPages > 1 && (
            <Pagination
              activePages={pagination.currentPage}
              numberOfPages={pagination.totalPages}
              nextPage={nextPage}
              prevPage={prevPage}
              nextSection={nextSection}
              prevSection={prevSection}
              selectPage={selectPage}
              dataTest="page-number"
            />
          )}
        </SitesFooterWrapper>
      </SitesTableWrapper>
      <Modal
        width="496px"
        isOpen={
          Boolean(siteToWorkWith.id) &&
          !siteToWorkWith.revertSupersededModal &&
          isKbaStatusEligibleForSupersedeModal(siteToWorkWith)
        }
        toggleModal={setSiteToWorkWith}
        modalTitleId="supersede"
        bottomBorder={false}
        padding="0 32px 32px 32px"
      >
        <SiteSupersedeModal site={siteToWorkWith} toggleModal={setSiteToWorkWith} getSite={getSites} />
      </Modal>
      <Modal
        width="496px"
        isOpen={
          Boolean(siteToWorkWith.id) &&
          (siteToWorkWith.SiteStatus?.id === SITE_STATUS_SUPERSEDED_ID ||
            siteToWorkWith.revertSupersededModal === true)
        }
        toggleModal={setSiteToWorkWith}
        modalTitleId="changeStatus"
        bottomBorder={false}
        padding="0 32px 32px 32px"
      >
        <SiteRevertStatusModal site={siteToWorkWith} toggleModal={setSiteToWorkWith} getSite={getSites} />
      </Modal>
      <Modal
        width="496px"
        isOpen={siteToWorkWith.id && canReviewSites(profile.RoleId)}
        toggleModal={setSiteToWorkWith}
        modalTitleId="approveForNomination"
      >
        <FlowRequestModal
          cancelHandler={() => {
            setSiteToWorkWith(false)
          }}
          confirmHandler={rfpAction}
          confirmBtnText="approve"
          text={<FormattedMessage id="approveForNominationText" values={{ siteName: <b>{siteToWorkWith.name}</b> }} />}
          item={siteToWorkWith}
          approve={true}
        />
      </Modal>
      <Modal
        width="496px"
        isOpen={siteToReject.id && canReviewSites(profile.RoleId)}
        toggleModal={setSiteToReject}
        modalTitleId="returnDraft"
      >
        <FlowRequestModal
          cancelHandler={() => {
            setSiteToReject(false)
          }}
          confirmHandler={rfpAction}
          confirmBtnText="returnToDraft"
          text={<FormattedMessage id="returnFeedbackText" values={{ siteName: <b>{siteToReject.name}</b> }} />}
          item={siteToReject}
          approve={false}
        />
      </Modal>
      <Modal
        width="496px"
        isOpen={siteToWorkWith.id && (profile.RoleId === INDIVIDUAL_ROLE_ID || profile.RoleId === NCG_ROLE_ID)}
        toggleModal={setSiteToWorkWith}
        modalTitleId="nominateAssessment"
      >
        <FlowRequestModal
          cancelHandler={() => {
            setSiteToWorkWith(false)
          }}
          confirmHandler={nominateSite}
          confirmBtnText="nominate"
          text={<FormattedMessage id="nominateSiteText" values={{ siteName: <b>{siteToWorkWith.name}</b> }} />}
          item={siteToWorkWith}
          approve={true}
        />
      </Modal>
      <Modal
        width="496px"
        isOpen={siteToPublish.isApproved === true}
        toggleModal={setSiteToPublish}
        modalTitleId="confirmAssessment"
      >
        <FlowRequestModal
          cancelHandler={() => {
            setSiteToPublish(false)
          }}
          confirmHandler={publishAction}
          confirmBtnText="confirm"
          text={<FormattedMessage id="confirmAssessmentText" values={{ siteName: <b>{siteToPublish.name}</b> }} />}
          item={siteToPublish}
          approve={true}
        />
      </Modal>
      <Modal
        width="496px"
        isOpen={approveEditOnlyCallBack.isApproved === true}
        toggleModal={setApproveEditOnlyCallBack}
        modalTitleId="approveEdits"
      >
        <FlowRequestModal
          cancelHandler={() => {
            setApproveEditOnlyCallBack(false)
          }}
          confirmHandler={approveEditsAction}
          confirmBtnText="confirm"
          text={<FormattedMessage id="approveEditsText" values={{ siteName: <b>{approveEditOnlyCallBack.name}</b> }} />}
          item={approveEditOnlyCallBack}
          approve={true}
        />
      </Modal>
      <Modal
        width="496px"
        isOpen={approveEditOnlyCallBack.isApproved === false}
        toggleModal={setApproveEditOnlyCallBack}
        modalTitleId="rejectEdits"
      >
        <FlowRequestModal
          cancelHandler={() => {
            setApproveEditOnlyCallBack(false)
          }}
          confirmHandler={approveEditsAction}
          confirmBtnText="rejectBtn"
          text={<FormattedMessage id="rejectEditsText" values={{ siteName: <b>{approveEditOnlyCallBack.name}</b> }} />}
          item={approveEditOnlyCallBack}
          approve={false}
        />
      </Modal>
      <Modal
        width="496px"
        isOpen={siteToPublish.isApproved === false}
        toggleModal={setSiteToPublish}
        modalTitleId="rejectAssessment"
      >
        <FlowRequestModal
          cancelHandler={() => {
            setSiteToPublish(false)
          }}
          confirmHandler={publishAction}
          confirmBtnText="rejectBtn"
          text={<FormattedMessage id="rejectAssessmentText" values={{ siteName: <b>{siteToPublish.name}</b> }} />}
          item={siteToPublish}
          approve={false}
        />
      </Modal>
      <Modal width="496px" isOpen={siteToDelist.delist} toggleModal={closeDelistModal} modalTitleId="delistAssessment">
        <FlowRequestModal
          cancelHandler={closeDelistModal}
          confirmHandler={delistSite}
          confirmBtnText="delistBtn"
          text={<FormattedMessage id="delistAssessmentText" values={{ siteName: <b>{siteToDelist.name}</b> }} />}
          item={siteToDelist}
          approve={false}
        />
      </Modal>
      <Modal
        width="496px"
        isOpen={isSiteDoesNotQualified}
        toggleModal={closeDoesNotQualifyModal}
        modalTitleId="doesNotQualifyModal"
      >
        <FlowRequestModal
          cancelHandler={closeDoesNotQualifyModal}
          confirmHandler={doesNotQualify}
          confirmBtnText="confirm"
          text={<FormattedMessage id="doesNotQualifyText" values={{ siteName: <b>{isSiteDoesNotQualified.name}</b> }} />}
          item={isSiteDoesNotQualified}
          approve={true}
        />
      </Modal>
      <Modal
        width="496px"
        isOpen={editOnlySiteToReject}
        toggleModal={setEditOnlySiteToReject}
        modalTitleId='deleteEditSiteOnly'
      >
        <FlowRequestModal
          cancelHandler={() => {
            setEditOnlySiteToReject(false)
          }}
          confirmHandler={deleteEditOnlySite}
          confirmBtnText="yesDelete"
          text={<FormattedMessage id="editSiteForDeletingBody" values={{ siteName: <b>{editOnlySiteToReject.name}</b> }} />}
          item={editOnlySiteToReject}
          approve={false}
        />
      </Modal>
      <Modal
        width="729px"
        height="556px"
        isOpen={reassessmentSites.length > 0 || biodiversitySites.length > 0}
        toggleModal={setEmptyArray}
        modalTitleId={modalTitleId}
        padding="32px"
        bottomBorder={true}
        infoHeader={reassessmentSites[0]?.name}
        assessmentValue={assessmentValue}
        paddingBottom="12px"
        paddingTop="12px"
        marginLeft="none"
      >
        <AssessmentModal sites={reassessmentSites}
          biodiversitySites={biodiversitySites || []}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          profile={profile}
          setSiteForDeleting={setSiteForDeleting}
          setEditOnlySiteToReject={setEditOnlySiteToReject} />

      </Modal>
      <Modal width="496px" isOpen={siteForDeleting.id} toggleModal={setSiteForDeleting} modalTitleId="deleteDraft">
        <FlowRequestModal
          cancelHandler={() => {
            setSiteForDeleting(false)
          }}
          confirmHandler={deleteSite}
          confirmBtnText="yesDelete"
          text={<FormattedMessage id="siteForDeletingBody" values={{ siteName: <b>{siteForDeleting.name}</b> }} />}
          item={siteForDeleting}
          approve={false}
        />
      </Modal>
      <Modal width="496px" isOpen={monitoringForDeleting.id} toggleModal={setMonitoringForDeleting} modalTitleId="deleteDraft">
        <FlowRequestModal
          cancelHandler={() => {
            setMonitoringForDeleting(false)
          }}
          confirmHandler={deleteMonitoring}
          confirmBtnText="yesDelete"
          text={<FormattedMessage id="monitoringForDeletingDraft" />}
          item={monitoringForDeleting}
          approve={false}
        />
      </Modal>
      <Modal width="496px" isOpen={monitoringForReverting.id} toggleModal={setMonitoringForReverting} modalTitleId="revertSubmission">
        <FlowRequestModal
          cancelHandler={() => {
            setMonitoringForReverting(false)
          }}
          confirmHandler={revertMonitoring}
          confirmBtnText="yesRevert"
          text={<FormattedMessage id="monitoringEvaluationRevertConfirmation" />}
          item={monitoringForReverting}
          approve={false}
        />
      </Modal>
    </>
  )
}

export default SitesManagement